import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  CalendarX,
  Camera,
  Clock,
  History,
  Loader2,
  MapPin,
  Paperclip,
  Play,
  Square,
} from "lucide-react";
import { GEOFENCE_RADIUS_METERS } from "@/lib/geolocation";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ClockHelpButton } from "@/components/clock/ClockHelpButton";
import { AddToHomeScreenButton } from "@/components/clock/AddToHomeScreenButton";

/**
 * Visual-only preview of the clock-in screen.
 * Uses mocked data — no server calls. Useful for tweaking the UI.
 * URL: /clock/demo
 */
export const Route = createFileRoute("/clock/demo")({
  component: ClockDemoPage,
  head: () => ({
    meta: [{ title: "Clock-in (Demo)" }, { name: "robots", content: "noindex" }],
  }),
});

const DEMO_CTX = {
  profile: { fullName: "João da Silva", email: "joao@empresa.com" },
  company: { name: "Construtora Demo Ltda" },
  projects: [
    { id: "p1", code: "OBR-001", name: "Edifício Aurora" },
    { id: "p2", code: "OBR-002", name: "Residencial Park" },
  ],
  jobSites: [
    {
      id: "s1",
      name: "Canteiro Principal",
      project_id: "p1",
      latitude: -23.55,
      longitude: -46.63,
      address_line1: "Av. Paulista, 1000",
      city: "São Paulo",
      state: "SP",
      postal_code: "01310-100",
      country: "BR",
    },
    {
      id: "s2",
      name: "Almoxarifado",
      project_id: "p1",
      latitude: null,
      longitude: null,
      address_line1: "Rua das Obras, 250",
      city: "São Paulo",
      state: "SP",
      postal_code: "04567-000",
      country: "BR",
    },
    {
      id: "s3",
      name: "Obra Bloco A",
      project_id: "p2",
      latitude: -23.56,
      longitude: -46.64,
      address_line1: "Alameda Santos, 500",
      city: "São Paulo",
      state: "SP",
      postal_code: "01418-000",
      country: "BR",
    },
  ],
};

const DEMO_ABSENCES = [
  {
    id: "a1",
    absence_date: new Date(Date.now() - 86400000 * 3).toISOString().slice(0, 10),
    category: "sick" as const,
    reason: "Consulta médica de rotina.",
    status: "approved" as const,
    review_notes: "Atestado recebido.",
  },
  {
    id: "a2",
    absence_date: new Date(Date.now() - 86400000 * 10).toISOString().slice(0, 10),
    category: "personal" as const,
    reason: "Assunto familiar urgente.",
    status: "pending" as const,
    review_notes: null as string | null,
  },
];

function ClockDemoPage() {
  const { t } = useTranslation();
  const ctx = DEMO_CTX;

  const [active, setActive] = useState<{ clock_in: string } | null>(null);
  const [selJobSite, setSelJobSite] = useState("");
  const [notes, setNotes] = useState("");
  const [breakMin, setBreakMin] = useState(0);

  const [absDate, setAbsDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [absCategory, setAbsCategory] = useState<"sick" | "personal" | "other">("sick");
  const [absReason, setAbsReason] = useState("");

  const filteredJobSites = ctx.jobSites;

  const todayStr = new Date().toISOString().slice(0, 10);

  const handleClockIn = () => {
    if (!selJobSite) return toast.error(t("clock.errors.selectJobSite"));
    setActive({ clock_in: new Date().toISOString() });
    toast.success("(Demo) " + t("clock.toasts.punchedIn"));
  };

  const handleClockOut = () => {
    if (active?.clock_in) {
      const elapsedH = (Date.now() - new Date(active.clock_in).getTime()) / 3_600_000;
      if (elapsedH < 8) {
        const hh = Math.floor(elapsedH);
        const mm = Math.floor((elapsedH - hh) * 60);
        const ok = window.confirm(
          `Você já bateu entrada e trabalhou apenas ${hh}h${String(mm).padStart(2, "0")} (menos de 8h).\n\nAgora você está batendo SAÍDA. Deseja confirmar?`,
        );
        if (!ok) return;
      }
    }
    setActive(null);
    setBreakMin(0);
    setNotes("");
    toast.success("(Demo) " + t("clock.toasts.punchedOut"));
  };

  return (
    <div className="min-h-screen bg-background px-4 py-6">
      <div className="mx-auto max-w-md space-y-4">
        <div className="rounded-lg border border-dashed border-primary/40 bg-primary/5 p-3 text-center text-xs text-primary">
          {t("clock.demoBanner")}
        </div>

        <div className="relative text-center">
          <div className="absolute left-0 top-0">
            <ClockHelpButton />
          </div>
          <div className="absolute right-0 top-0">
            <LanguageSwitcher variant="ghost" size="sm" />
          </div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            {ctx.company.name}
          </p>
          <h1 className="mt-1 text-2xl font-bold">
            {t("clock.hello", { name: ctx.profile.fullName })}
          </h1>
          <div className="mt-3 flex justify-center">
            <AddToHomeScreenButton />
          </div>
        </div>

        <Tabs defaultValue="clock" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="clock" className="gap-1 text-xs">
              <Clock className="h-3.5 w-3.5" /> {t("clock.tabs.clock")}
            </TabsTrigger>
            <TabsTrigger value="absence" className="gap-1 text-xs">
              <CalendarX className="h-3.5 w-3.5" /> {t("clock.tabs.absence")}
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-1 text-xs">
              <History className="h-3.5 w-3.5" /> {t("clock.tabs.history")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="clock" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Clock className="h-5 w-5 text-primary" />
                  {active ? t("clock.openShift") : t("clock.clockIn")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {active ? (
                  <>
                    <div className="rounded-lg border border-border bg-muted/40 p-4">
                      <p className="text-sm text-muted-foreground">{t("clock.entry")}</p>
                      <p className="text-lg font-semibold">
                        {new Date(active.clock_in).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <Label htmlFor="break">{t("clock.break")}</Label>
                      <Input
                        id="break"
                        type="number"
                        min={0}
                        value={breakMin}
                        onChange={(e) => setBreakMin(Number(e.target.value) || 0)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="notes-out">{t("clock.notes")}</Label>
                      <Textarea
                        id="notes-out"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder={t("clock.optional")}
                        rows={2}
                      />
                    </div>
                    <Button onClick={handleClockOut} className="w-full gap-2" size="lg">
                      <Square className="h-4 w-4" />
                      {t("clock.punchOut")}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => toast.info("(Demo) Selfie capture")}
                      className="w-full gap-2"
                      size="lg"
                    >
                      <Camera className="h-4 w-4" />
                      {t("clock.punchOutSelfie")}
                    </Button>
                    <p className="text-center text-xs text-muted-foreground">
                      {t("clock.selfieHint")}
                    </p>
                  </>
                ) : (
                  <>
                    <div>
                      <Label>{t("clock.jobSite")}</Label>
                      <Select value={selJobSite} onValueChange={setSelJobSite}>
                        <SelectTrigger>
                          <SelectValue placeholder={t("clock.selectJobSite")} />
                        </SelectTrigger>
                        <SelectContent>
                          {filteredJobSites.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.name}
                              {s.latitude == null || s.longitude == null
                                ? ` ${t("clock.withoutGps")}`
                                : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {(() => {
                        const site = ctx.jobSites.find((s) => s.id === selJobSite);
                        if (!site) return null;
                        const address = [
                          site.address_line1,
                          [site.city, site.state].filter(Boolean).join(", "),
                          [site.postal_code, site.country].filter(Boolean).join(" "),
                        ]
                          .filter(Boolean)
                          .join(" • ");
                        if (!address) return null;
                        return (
                          <div className="mt-2 flex items-start gap-2 text-xs text-muted-foreground">
                            <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                            <span>{address}</span>
                          </div>
                        );
                      })()}
                    </div>
                    <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
                      <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <span>
                        <Trans
                          i18nKey="clock.gpsNotice"
                          values={{ meters: GEOFENCE_RADIUS_METERS }}
                          components={{ strong: <strong /> }}
                        />
                      </span>
                    </div>
                    <Button onClick={handleClockIn} className="w-full gap-2" size="lg">
                      <Play className="h-4 w-4" />
                      {t("clock.punchIn")}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => toast.info("(Demo) Selfie capture")}
                      className="w-full gap-2"
                      size="lg"
                    >
                      <Camera className="h-4 w-4" />
                      {t("clock.punchInSelfie")}
                    </Button>
                    <p className="text-center text-xs text-muted-foreground">
                      {t("clock.selfieHint")}
                    </p>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="absence" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <CalendarX className="h-5 w-5 text-primary" />
                  {t("clock.absence.title")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="abs-date">{t("clock.absence.date")}</Label>
                  <Input
                    id="abs-date"
                    type="date"
                    value={absDate}
                    max={todayStr}
                    onChange={(e) => setAbsDate(e.target.value)}
                  />
                </div>
                <div>
                  <Label>{t("clock.absence.category")}</Label>
                  <Select
                    value={absCategory}
                    onValueChange={(v) => setAbsCategory(v as typeof absCategory)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sick">{t("clock.absence.sick")}</SelectItem>
                      <SelectItem value="personal">{t("clock.absence.personal")}</SelectItem>
                      <SelectItem value="other">{t("clock.absence.other")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="abs-reason">{t("clock.absence.reason")}</Label>
                  <Textarea
                    id="abs-reason"
                    value={absReason}
                    onChange={(e) => setAbsReason(e.target.value)}
                    placeholder={t("clock.absence.reasonPlaceholder")}
                    rows={3}
                  />
                </div>
                <div>
                  <Label htmlFor="abs-file" className="flex items-center gap-1">
                    <Paperclip className="h-3.5 w-3.5" />
                    {t("clock.absence.attachment")}
                  </Label>
                  <Input id="abs-file" type="file" accept="image/*,application/pdf" />
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("clock.absence.attachmentHint")}
                  </p>
                </div>
                <Button
                  onClick={() => toast.success("(Demo) " + t("clock.absence.submitted"))}
                  className="w-full"
                  size="lg"
                >
                  {t("clock.absence.submit")}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <History className="h-5 w-5 text-primary" />
                  {t("clock.history.title")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {DEMO_ABSENCES.map((a) => (
                    <li key={a.id} className="rounded-lg border border-border p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold">
                            {new Date(`${a.absence_date}T00:00:00`).toLocaleDateString()}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {a.category === "sick"
                              ? t("clock.history.categorySick")
                              : a.category === "personal"
                                ? t("clock.history.categoryPersonal")
                                : t("clock.history.categoryOther")}
                          </p>
                        </div>
                        <AbsenceStatusBadge status={a.status} />
                      </div>
                      <p className="mt-2 text-xs">{a.reason}</p>
                      {a.review_notes && (
                        <p className="mt-2 rounded bg-muted/50 p-2 text-xs text-muted-foreground">
                          <strong>{t("clock.history.answer")}</strong> {a.review_notes}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <p className="text-center text-xs text-muted-foreground">{t("clock.footerSecure")}</p>
      </div>
    </div>
  );
}

function AbsenceStatusBadge({ status }: { status: "pending" | "approved" | "rejected" }) {
  const { t } = useTranslation();
  if (status === "approved") {
    return (
      <Badge variant="outline" className="border-primary/40 text-primary">
        {t("clock.history.approved")}
      </Badge>
    );
  }
  if (status === "rejected") {
    return (
      <Badge variant="outline" className="border-destructive/40 text-destructive">
        {t("clock.history.rejected")}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border-muted-foreground/40 text-muted-foreground">
      {t("clock.history.pending")}
    </Badge>
  );
}
